import React, { useState, useEffect } from 'react';
import { ShieldCheck, ClipboardList, Lock, Search } from 'lucide-react';
import { motion, animate } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import './NewHeroSection.css';

const CountingNumber = ({ value }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const controls = animate(0, value, {
      duration: 2.5,
      ease: "easeOut",
      onUpdate(v) {
        setCount(Math.round(v));
      }
    });
    return () => controls.stop();
  }, [value]);

  // Live fluctuation effect
  useEffect(() => {
    const interval = setInterval(() => {
      // Create a small random fluctuation between -2 and +2
      const delta = Math.floor(Math.random() * 5) - 2;
      setCount((prev) => {
        // We only fluctuate after reaching near the target to avoid messing up initial animation
        if (prev < value - 5) return prev;
        let next = prev + delta;
        // Keep within bounds
        if (next > 100) next = 100;
        if (next < value - 5) next = value - 5;
        return next;
      });
    }, 1800); // Trigger every 1.8 seconds

    return () => clearInterval(interval);
  }, [value]);

  return <>{count}</>;
};

const NewHeroSection = () => {
  const { theme } = useTheme();

  return (
    <section className="new-hero-section">
      <div className="new-hero-background">
        {theme === 'dark' ? (
          <img src={require('../../assets/img/dark-hero-bg.jpg')} onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1598387181032-a3103a2db5b3?auto=format&fit=crop&q=80&w=2000'; }} alt="Stage dark background" className="hero-bg-img" />
        ) : (
          <img src={require('../../assets/img/hero-bg.jpg')} onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1598387181032-a3103a2db5b3?auto=format&fit=crop&q=80&w=2000'; }} alt="Stage background" className="hero-bg-img" />
        )}
        <div className="hero-overlay"></div>
      </div>
      
      <div className="new-hero-content-wrapper">
        <div className="new-hero-left">
          <h1 className="hero-heading">
            Discover. Audition. <br/>
            <small className="text-red">Get Discovered.</small>
          </h1>
          <p className="hero-subheading">
            India's trusted platform for auditions, casting<br/>opportunities and talent discovery.
          </p>

          <div className="hero-features hero-features-grid">
            <div className="hero-feature-card">
              <span className="feature-icon text-orange"><ShieldCheck size={28} /></span>
              <div className="feature-text">
                <strong>Verified Opportunities</strong>
                <span>No Fake Auditions</span>
              </div>
            </div>
            <div className="hero-feature-card">
              <span className="feature-icon text-orange"><ClipboardList size={28} /></span>
              <div className="feature-text">
                <strong>Online & In-Person</strong>
                <span>Auditions</span>
              </div>
            </div>
            <div className="hero-feature-card">
              <span className="feature-icon text-orange"><ShieldCheck size={28} /></span>
              <div className="feature-text">
                <strong>Verified Opportunities</strong>
                <span>No Fake Auditions</span>
              </div>
            </div>
            <div className="hero-feature-card">
              <span className="feature-icon text-orange"><Lock size={28} /></span>
              <div className="feature-text">
                <strong>Secure Payments</strong>
                <span>100% Safe</span>
              </div>
            </div>
          </div>

          <div className="hero-popular-searches mt-4">
            <span className="popular-label">Popular Audition Types:</span>
            <div className="tags">
              <span className="tag">Web Series</span>
              <span className="tag">Short Films</span>
              <span className="tag">Movies</span>
              <span className="tag">TV Serials</span>
              <span className="tag">Advertisements</span>
              <span className="tag">Music Videos</span>
              <span className="tag">Voice Over</span>
              <span className="tag">Theatre</span>
            </div>
          </div>

          <div className="hero-trust-strip mt-4">
            <div className="trust-strip-header">
              <ShieldCheck size={20} className="text-green me-2" />
              <span className="text-orange fw-bold">TRUST STRIP:</span>
            </div>
            <div className="trust-strip-grid mt-3">
              <div className="trust-item"><ShieldCheck size={14} /> 100% Verified Auditions</div>
              <div className="trust-item"><ShieldCheck size={14} /> No Fake Opportunities</div>
              <div className="trust-item"><Lock size={14} /> Secure Payments</div>
              <div className="trust-item"><ShieldCheck size={14} /> Privacy Protected</div>
            </div>
            <div className="trust-strip-divider my-3"></div>
            <div className="trust-strip-footer d-flex justify-content-between align-items-center">
              <div className="trust-rating">
                <span className="text-orange fs-4 fw-bold me-2">4.9/5</span>
                <span className="text-orange">★★★★★</span>
              </div>
              <div className="trust-reviews text-end" style={{fontSize: '0.75rem', color: '#94a3b8', lineHeight: '1.2'}}>
                Based on 2,000+<br/>Reviews
              </div>
            </div>
          </div>
        </div>

        <div className="new-hero-right">
          <div className="hero-image-wrapper">
             <img src={require('../../assets/img/hero-woman.png')} onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/600x800/transparent/111?text=Woman+with+Clapperboard'; }} alt="Woman holding clapperboard" className="hero-woman-img" />
          </div>
          
          <div className="ai-score-widget">
            <div className="ai-score-header">AI PERFORMANCE SCORE</div>
            <div className="ai-score-circle-wrapper">
              <div className="ai-score-circle">
                <div className="ai-score-circle-inner">
                  <span className="score-number"><CountingNumber value={87} /></span>
                  <span className="score-total">/100</span>
                </div>
              </div>
            </div>
            <div className="ai-score-status text-green">Great Performance!</div>
            
            <div className="ai-metrics">
              <div className="ai-metric">
                <span className="metric-label">Emotion</span>
                <div className="metric-bar-wrapper">
                  <motion.div className="metric-bar" initial={{ width: 0 }} animate={{ width: '88%' }} transition={{ duration: 2, delay: 0.2 }}></motion.div>
                </div>
                <span className="metric-val"><CountingNumber value={88} /></span>
              </div>
              <div className="ai-metric">
                <span className="metric-label">Voice Clarity</span>
                <div className="metric-bar-wrapper">
                  <motion.div className="metric-bar" initial={{ width: 0 }} animate={{ width: '86%' }} transition={{ duration: 2, delay: 0.3 }}></motion.div>
                </div>
                <span className="metric-val"><CountingNumber value={86} /></span>
              </div>
              <div className="ai-metric">
                <span className="metric-label">Confidence</span>
                <div className="metric-bar-wrapper">
                  <motion.div className="metric-bar" initial={{ width: 0 }} animate={{ width: '90%' }} transition={{ duration: 2, delay: 0.4 }}></motion.div>
                </div>
                <span className="metric-val"><CountingNumber value={90} /></span>
              </div>
              <div className="ai-metric">
                <span className="metric-label">Expression</span>
                <div className="metric-bar-wrapper">
                  <motion.div className="metric-bar" initial={{ width: 0 }} animate={{ width: '85%' }} transition={{ duration: 2, delay: 0.5 }}></motion.div>
                </div>
                <span className="metric-val"><CountingNumber value={85} /></span>
              </div>
            </div>

            <button className="btn-outline-dark ai-try-btn">
              Try AI Studio <span className="arrow-right">→</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewHeroSection;
